/* eslint-disable import/no-cycle */
import React, { useEffect, useMemo, useState } from 'react';
import { IoMdClose } from 'react-icons/io';
import { BiPencil, BiTrash } from 'react-icons/bi';
import { useFormik } from 'formik';
import { RiAddCircleFill, RiIndeterminateCircleFill } from 'react-icons/ri';
import { AiOutlineClockCircle } from 'react-icons/ai';
import moment from 'moment';

import OutlinedButton from 'components/layout/form/outlinedButton';
import ContainedButton from 'components/layout/form/containedButton';
import TextSelect from 'components/layout/form/TextSelect';
import { IData } from '..';

import {
  Content,
  ContentAddPlus,
  ContentButton,
  Day,
  Days,
  Footer,
  Header,
  Interval,
  Intervals,
  IntervalTitle,
  Item,
  ItemTitle,
  List,
  PrimaryPart,
  SecondyPart,
  SelectsContent,
  TextIntervals,
  Title,
} from './styles';
import { Divider } from '../styles';

interface IProps {
  onClose: () => void;
  data: IData[];
  dataProvisional: IData[];
  setDataProvisional: React.Dispatch<React.SetStateAction<IData[]>>;
  openModalConfirm: () => void;
}

interface IDates {
  label: string;
  value: string;
  labelList: string;
  intervals?: {
    beginTime: string;
    endTime: string;
  }[];
}

const datesTemplate = [
  {
    label: 'D',
    value: 'SUNDAY',
    labelList: 'Domingo',
    intervals: [],
  },
  {
    label: 'S',
    value: 'MONDAY',
    labelList: 'Segunda-feira',
    intervals: [],
  },
  {
    label: 'T',
    value: 'TUESDAY',
    labelList: 'Terça-feira',
    intervals: [],
  },
  {
    label: 'Q',
    value: 'WEDNESDAY',
    labelList: 'Quarta-feira',
    intervals: [],
  },
  {
    label: 'Q',
    value: 'THURSDAY',
    labelList: 'Quinta-feira',
    intervals: [],
  },
  {
    label: 'S',
    value: 'FRIDAY',
    labelList: 'Sexta-feira',
    intervals: [],
  },
  {
    label: 'S',
    value: 'SATURDAY',
    labelList: 'Sábado',
    intervals: [],
  },
];

const ModalContentBestTimes = ({
  onClose,
  data,
  dataProvisional,
  setDataProvisional,
  openModalConfirm,
}: IProps): JSX.Element => {
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [isInterval2, setIsInterval2] = useState(false);
  const [dates, setDates] = useState<IDates[]>([]);
  const [isEditing, setIsEditing] = useState<IDates | null>(null);

  const formik = useFormik({
    initialValues: {
      interval1BeginTime: 'vazio',
      interval1EndTime: 'vazio',
      interval2BeginTime: 'vazio',
      interval2EndTime: 'vazio',
    },
    onSubmit: async () => {
      const datesNew = dates?.map((item) => ({
        weekDay: item.value,
        intervals: item.intervals,
      }));

      setDataProvisional(datesNew);
      openModalConfirm();
      onClose();
    },
  });

  useEffect(() => {
    setLoading(true);

    const result = datesTemplate.map((item) => {
      const isIntervalsIndex = (dataProvisional ?? data)?.findIndex(
        (item2) => item2?.weekDay === item?.value
      );

      return {
        ...item,
        intervals:
          isIntervalsIndex >= 0
            ? (dataProvisional ?? data)[isIntervalsIndex]?.intervals
            : [],
      };
    });

    setDates(result);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isEditing !== null) {
      setSelectedDays([isEditing.value]);
      if (isEditing.intervals) {
        isEditing.intervals.map((item, index) => {
          formik.setFieldValue(`interval${index + 1}BeginTime`, item.beginTime);
          formik.setFieldValue(`interval${index + 1}EndTime`, item.endTime);
          return null;
        });
        if (isEditing.intervals.length > 1) {
          setIsInterval2(true);
        }
      }
    }
  }, [isEditing]);

  const onClickDay = (d: string): void => {
    let selectedDaysData = selectedDays;

    if (selectedDaysData.includes(d)) {
      const selectedDaysDataExcluded = selectedDaysData?.filter(
        (item) => item !== d
      );

      selectedDaysData = selectedDaysDataExcluded;
    } else {
      selectedDaysData = [...selectedDaysData, d];
    }

    setSelectedDays(selectedDaysData);
  };

  const get30Minutes = (hour: number, minute: number): string[] => {
    const maxTime = moment().set({
      minutes: 30,
      hours: 23,
      seconds: 0,
    });

    const minTime = moment().set({
      hours: hour || 0,
      minutes: minute || 0,
      seconds: 0,
    });

    const listTime = [];

    for (
      let i = minTime;
      i <= maxTime;
      minTime.add({
        minutes: 30,
      })
    ) {
      listTime.push(i.format('HH:mm'));
    }

    return listTime;
  };

  const addingIntervals = (): void => {
    const datesResult = dates?.map((item) => {
      if (selectedDays?.includes(item?.value)) {
        const itemData = {
          ...item,
          intervals: [
            {
              beginTime: formik.values.interval1BeginTime,
              endTime: formik.values.interval1EndTime,
            },
          ],
        };

        if (isInterval2) {
          itemData.intervals.push({
            beginTime: formik.values.interval2BeginTime,
            endTime: formik.values.interval2EndTime,
          });
        }

        return itemData;
      }

      return item;
    });

    formik.resetForm();
    setIsInterval2(false);
    setSelectedDays([]);
    setDates(datesResult);
  };

  const removeIntervals = (): void => {
    if (isEditing?.intervals) {
      isEditing.intervals.map((_, index) => {
        formik.setFieldValue(`interval${index + 1}BeginTime`, 'vazio');
        formik.setFieldValue(`interval${index + 1}EndTime`, 'vazio');
        return null;
      });
      if (isEditing.intervals.length > 1) {
        setIsInterval2(false);
      }
    }
  };

  const saveIntervalsEditing = (): void => {
    formik.setSubmitting(true);

    if (isEditing) {
      const datesResult = dates.map((item) => {
        if (item.value === isEditing.value) {
          const itemData = {
            ...item,
            intervals: [
              {
                beginTime: formik.values.interval1BeginTime,
                endTime: formik.values.interval1EndTime,
              },
            ],
          };

          if (isInterval2) {
            itemData.intervals.push({
              beginTime: formik.values.interval2BeginTime,
              endTime: formik.values.interval2EndTime,
            });
          }

          return itemData;
        }

        return item;
      });

      setDates(datesResult);
    }

    if (isEditing?.intervals) {
      isEditing.intervals.map((_, index) => {
        formik.setFieldValue(`interval${index + 1}BeginTime`, 'vazio');
        formik.setFieldValue(`interval${index + 1}EndTime`, 'vazio');
        return null;
      });

      setIsInterval2(false);
    }

    setIsEditing(null);
    setSelectedDays(['']);
    formik.setSubmitting(false);
  };

  const cancelIntervalsEditing = (): void => {
    if (isEditing?.intervals) {
      isEditing.intervals.map((_, index) => {
        formik.setFieldValue(`interval${index + 1}BeginTime`, 'vazio');
        formik.setFieldValue(`interval${index + 1}EndTime`, 'vazio');
        return null;
      });
    }

    setSelectedDays(['']);
    setIsEditing(null);
    setIsInterval2(false);
  };

  const isEqualsDatas = useMemo(() => {
    const datesNew = dates
      ?.filter((item) => item?.intervals?.length)
      ?.map((item) => ({
        weekDay: item.value,
        intervals: item.intervals,
      }));

    return JSON.stringify(datesNew) === JSON.stringify(dataProvisional || data);
  }, [data, dataProvisional, dates]);

  const isEqualsIntervals = useMemo(() => {
    let isEquals = true;

    if (isEditing) {
      const datesIntervals = dates.find(
        (item) => item.value === isEditing.value
      )?.intervals;

      if (
        formik.values.interval1BeginTime !== 'vazio' ||
        formik.values.interval1EndTime
      ) {
        const isEditingIntervals = [
          {
            beginTime: formik.values.interval1BeginTime,
            endTime: formik.values.interval1EndTime,
          },
        ];

        if (isInterval2) {
          isEditingIntervals.push({
            beginTime: formik.values.interval2BeginTime,
            endTime: formik.values.interval2EndTime,
          });
        }

        if (isEditingIntervals?.length !== datesIntervals?.length) {
          isEquals = false;
        } else {
          datesIntervals.map((item, index) => {
            if (
              item.beginTime !== isEditingIntervals[index].beginTime ||
              item.endTime !== isEditingIntervals[index].endTime
            ) {
              isEquals = false;
            }

            return null;
          });
        }
      }
    }

    return isEquals;
  }, [dates, isEditing, formik.values]);

  const deleteItem = (item: IDates): void => {
    const datesNew = dates?.filter((item2) => {
      if (item2.value === item.value) {
        item2.intervals = [];
      }
      return item2;
    });

    setDates(datesNew);
  };

  const isEmptyAllIntervals = useMemo(() => {
    const isEmpty = dates?.filter((item) => item?.intervals?.length);

    return !(isEmpty?.length > 0);
  }, [dates]);

  const isEditingIntervals = useMemo(() => {
    if (
      isInterval2 &&
      (formik.values.interval2BeginTime === 'vazio' ||
        formik.values.interval2EndTime === 'vazio' ||
        formik.values.interval1BeginTime === 'vazio' ||
        formik.values.interval1EndTime === 'vazio')
    ) {
      return true;
    }
    if (
      !isInterval2 &&
      (formik.values.interval1BeginTime === 'vazio' ||
        formik.values.interval1EndTime === 'vazio')
    ) {
      return true;
    }

    return false;
  }, [formik, isInterval2]);

  return (
    <>
      <Header>
        <Title>Selecionar dias e horários</Title>
        <IoMdClose onClick={onClose} />
      </Header>
      <Content>
        <Days>
          {dates?.map((date) => {
            const isSelected = !!selectedDays?.find((d) => d === date?.value);
            const isDisabled =
              (date?.intervals && date?.intervals?.length > 0) ||
              !!isEditing ||
              date?.label === 'D';

            return (
              <Day
                onClick={() => onClickDay(date?.value)}
                selected={isSelected}
                disabled={isDisabled}
                key={date?.value}
              >
                {date?.label}
              </Day>
            );
          })}
        </Days>
        <form onSubmit={formik.submitForm}>
          <Intervals>
            <Interval>
              <IntervalTitle>
                <span>Intervalo 1</span>
                {!isInterval2 && (
                  <ContentAddPlus mobile>
                    <RiAddCircleFill onClick={() => setIsInterval2(true)} />
                  </ContentAddPlus>
                )}
              </IntervalTitle>
              <SelectsContent>
                <TextSelect
                  name="interval1BeginTime"
                  type="text"
                  label="Hora inicial"
                  value={formik.values.interval1BeginTime}
                  onChange={(e) => {
                    formik.setFieldValue('interval2BeginTime', 'vazio');
                    formik.setFieldValue('interval2EndTime', 'vazio');
                    formik.handleChange(e);
                  }}
                  onBlur={formik.handleBlur}
                  errorCondition={
                    !!formik.errors.interval1BeginTime &&
                    formik.touched.interval1BeginTime
                  }
                  errorMessage={formik.errors.interval1BeginTime}
                  LeadingIcon={<AiOutlineClockCircle />}
                  disabled={!selectedDays?.length}
                >
                  <option disabled value="vazio">
                    Selecione
                  </option>
                  {get30Minutes(0, 0)?.map((item) => {
                    return (
                      <option key={`interval1BeginTime-${item}`} value={item}>
                        {item}
                      </option>
                    );
                  })}
                </TextSelect>
                <TextSelect
                  name="interval1EndTime"
                  type="text"
                  label="Hora final"
                  value={formik.values.interval1EndTime}
                  onChange={(e) => {
                    formik.setFieldValue('interval2EndTime', 'vazio');
                    formik.handleChange(e);
                  }}
                  onBlur={formik.handleBlur}
                  errorCondition={
                    !!formik.errors.interval1EndTime &&
                    formik.touched.interval1EndTime
                  }
                  errorMessage={formik.errors.interval1EndTime}
                  LeadingIcon={<AiOutlineClockCircle />}
                  disabled={!selectedDays?.length}
                >
                  <option disabled value="vazio">
                    Selecione
                  </option>
                  {(() => {
                    if (formik.values.interval1BeginTime !== 'vazio') {
                      const valueSplit =
                        formik.values.interval1BeginTime.split(':');

                      const momentString = moment()
                        .set({
                          hour: Number(valueSplit[0]),
                          minutes: Number(valueSplit[1]),
                          seconds: 0,
                        })
                        .add({
                          minutes: 150,
                        });

                      return get30Minutes(
                        momentString.hours(),
                        momentString.minutes()
                      )?.map((item) => {
                        return (
                          <option key={`interval1EndTime-${item}`} value={item}>
                            {item}
                          </option>
                        );
                      });
                    }

                    return null;
                  })()}
                </TextSelect>
                {!isInterval2 && (
                  <ContentAddPlus>
                    <RiAddCircleFill onClick={() => setIsInterval2(true)} />
                  </ContentAddPlus>
                )}
              </SelectsContent>
            </Interval>
            {isInterval2 && (
              <Interval>
                <IntervalTitle>
                  <span>Intervalo 2</span>

                  <ContentAddPlus mobile>
                    <RiIndeterminateCircleFill
                      onClick={() => setIsInterval2(false)}
                    />
                  </ContentAddPlus>
                </IntervalTitle>
                <SelectsContent>
                  <TextSelect
                    name="interval2BeginTime"
                    type="text"
                    label="Hora inicial"
                    value={formik.values.interval2BeginTime}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    errorCondition={
                      !!formik.errors.interval2BeginTime &&
                      formik.touched.interval2BeginTime
                    }
                    errorMessage={formik.errors.interval2BeginTime}
                    LeadingIcon={<AiOutlineClockCircle />}
                    disabled={!selectedDays?.length}
                  >
                    <option disabled value="vazio">
                      Selecione
                    </option>
                    {(() => {
                      if (formik.values.interval1EndTime !== 'vazio') {
                        const valueSplit =
                          formik.values.interval1EndTime.split(':');

                        const momentString = moment()
                          .set({
                            hour: Number(valueSplit[0]),
                            minutes: Number(valueSplit[1]),
                            seconds: 0,
                          })
                          .add({
                            minutes: 150,
                          });

                        return get30Minutes(
                          momentString.hours(),
                          momentString.minutes()
                        )?.map((item) => {
                          return (
                            <option
                              key={`interval2EndTime-${item}`}
                              value={item}
                            >
                              {item}
                            </option>
                          );
                        });
                      }

                      return null;
                    })()}
                  </TextSelect>
                  <TextSelect
                    name="interval2EndTime"
                    type="text"
                    label="Hora final"
                    value={formik.values.interval2EndTime}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    errorCondition={
                      !!formik.errors.interval2EndTime &&
                      formik.touched.interval2EndTime
                    }
                    errorMessage={formik.errors.interval2EndTime}
                    LeadingIcon={<AiOutlineClockCircle />}
                    disabled={!selectedDays?.length}
                  >
                    <option disabled value="vazio">
                      Selecione
                    </option>
                    {(() => {
                      if (formik.values.interval2BeginTime !== 'vazio') {
                        const valueSplit =
                          formik.values.interval2BeginTime.split(':');

                        const momentString = moment()
                          .set({
                            hour: Number(valueSplit[0]),
                            minutes: Number(valueSplit[1]),
                            seconds: 0,
                          })
                          .add({
                            minutes: 150,
                          });

                        return get30Minutes(
                          momentString.hours(),
                          momentString.minutes()
                        )?.map((item) => {
                          return (
                            <option
                              key={`interval2EndTime-${item}`}
                              value={item}
                            >
                              {item}
                            </option>
                          );
                        });
                      }

                      return null;
                    })()}
                  </TextSelect>

                  <ContentAddPlus>
                    <RiIndeterminateCircleFill
                      onClick={() => setIsInterval2(false)}
                    />
                  </ContentAddPlus>
                </SelectsContent>
              </Interval>
            )}
            <ContentButton>
              <ContainedButton
                disabled={
                  !selectedDays?.length ||
                  (!isEditing && isInterval2
                    ? formik.values.interval1BeginTime === 'vazio' ||
                      formik.values.interval1EndTime === 'vazio' ||
                      formik.values.interval2BeginTime === 'vazio' ||
                      formik.values.interval2EndTime === 'vazio'
                    : formik.values.interval1BeginTime === 'vazio' ||
                      formik.values.interval1EndTime === 'vazio')
                }
                onClick={() => {
                  if (isEditing) {
                    if (isEditing?.intervals) {
                      isEditing.intervals.map((_, index) => {
                        formik.setFieldValue(
                          `interval${index + 1}BeginTime`,
                          'vazio'
                        );
                        formik.setFieldValue(
                          `interval${index + 1}EndTime`,
                          'vazio'
                        );
                        return null;
                      });
                    }
                    deleteItem(isEditing);
                    setIsEditing(null);
                    setSelectedDays(['']);
                  } else {
                    addingIntervals();
                  }
                }}
              >
                {isEditing ? 'Remover intervalo(s)' : 'Adicionar intervalo(s)'}
              </ContainedButton>
            </ContentButton>
          </Intervals>
        </form>
        {dates?.length && !isEditing && (
          <List>
            {dates?.map((item) => {
              if (item?.intervals?.length) {
                return (
                  <Item key={`list-item-modal-${item?.value}`}>
                    <PrimaryPart>
                      <ItemTitle>{item?.labelList}</ItemTitle>
                      <TextIntervals>
                        {item?.intervals?.map(
                          (item2, index: number) =>
                            `${item2?.beginTime} - ${item2?.endTime}${
                              item?.intervals &&
                              item?.intervals?.length > 1 &&
                              index === 0
                                ? ' / '
                                : ''
                            }`
                        )}
                      </TextIntervals>
                    </PrimaryPart>
                    <SecondyPart>
                      <BiTrash onClick={() => deleteItem(item)} />
                      <BiPencil onClick={() => setIsEditing(item)} />
                    </SecondyPart>
                  </Item>
                );
              }

              return null;
            })}
          </List>
        )}
      </Content>
      <Divider />
      <Footer>
        <OutlinedButton
          onClick={() => {
            if (isEditing) {
              cancelIntervalsEditing();
            } else {
              onClose();
            }
          }}
          disabled={formik?.isSubmitting}
        >
          {isEditing ? 'Cancelar edição' : 'Cancelar'}
        </OutlinedButton>
        <ContainedButton
          onClick={() => {
            if (isEditing) {
              saveIntervalsEditing();
            } else {
              formik.handleSubmit();
            }
          }}
          disabled={
            formik?.isSubmitting ||
            isEmptyAllIntervals ||
            (isEditing && isEditingIntervals && isEqualsIntervals)
          }
        >
          {isEditing ? 'Salvar edição' : 'Salvar horário(s)'}
        </ContainedButton>
      </Footer>
    </>
  );
};

export default ModalContentBestTimes;
